import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    input,
    model,
    OnChanges,
    OnDestroy,
    OnInit,
    output,
    SimpleChanges,
    viewChild
} from '@angular/core';
import { DetailViewState } from '../overview';
import {
    AixButtonComponent,
    AixDataTestingDirective,
    AixHeaderSectionComponent,
    AixNotificationComponent,
    AixRadioGroupComponent,
    BUTTON_TYPE
} from '@trade-platform/ui-components';
import {
    BaseOrder,
    eSignEnvelopeRecipient,
    isFeatureEnabled,
    OrderDeliveryType
} from '@trade-platform/ui-shared';
import { idCheckType, recipientType } from '@trade-platform/lib-enums';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { BaseOrdersStoreFacade, ORDERS_STORE_FACADE } from '../../../base.orders.store.facade';
import { SigningMethodPreferenceService } from '../../../store/order-signing-method/signing-method-preference.service';

@Component({
    selector: 'aix-delivery',
    templateUrl: './delivery.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AixHeaderSectionComponent,
        AixDataTestingDirective,
        AixNotificationComponent,
        FormsModule,
        ReactiveFormsModule,
        AixRadioGroupComponent,
        AixButtonComponent
    ]
})
export class AixOrderProcessDeliveryComponent implements OnInit, OnChanges, OnDestroy {
    storeFacade = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
    private ref = inject(ChangeDetectorRef);
    private _fb = inject(UntypedFormBuilder);
    private signingMethodPreference = inject(SigningMethodPreferenceService);

    notification = viewChild<AixNotificationComponent>(AixNotificationComponent);
    order = input<BaseOrder>();
    isReadOnly = input<boolean | null | undefined>(false);
    wetSignOnly = model<boolean>(false);
    // Whether this order has other eligible orders to bundle with (aix-ui#5491). Drives whether the
    // bundle option in "Choose Signing Method" is enabled.
    hasBundleCandidates = input<boolean>(false);

    // The bundle option is shown only when the per-user bundledSignatures feature is granted.
    readonly bundleSignaturesEnabled = isFeatureEnabled('bundledSignatures');

    onMakeChanges = output();
    clickContinue = output<DetailViewState>();

    deliveryForm: UntypedFormGroup;
    deliveryMethod: OrderDeliveryType;
    deliveryMethods = [
        {
            value: 'docu-sign' as OrderDeliveryType,
            title: 'Send for eSign',
            description: 'Send documents for eSignature.',
            disabled: false
        },
        {
            value: 'wet-sign' as OrderDeliveryType,
            title: 'Download & Sign',
            description: 'Download and sign forms.',
            disabled: false
        }
    ];

    continueButtonType = BUTTON_TYPE.primary;

    subscriptions: Subscription[] = [];

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.deliveryForm = this._fb.group({
            deliveryMethod: this._fb.control({}, Validators.required)
        });
    }

    ngOnInit() {
        // Add the bundle option to the "Choose Signing Method" list only when the feature is enabled.
        if (this.bundleSignaturesEnabled && !this.deliveryMethods.some(d => d.value === 'bundle')) {
            this.deliveryMethods.push({
                value: 'bundle' as OrderDeliveryType,
                title: 'Sign with other orders',
                description:
                    'Bundle this order with other eligible orders into one DocuSign package (one investor email).',
                disabled: true
            });
            // #5582: recompute the option's disabled state immediately. Angular runs ngOnChanges BEFORE
            // ngOnInit, so the first (and, if the inputs never change again, only) ngOnChanges ran while
            // this option was still absent and could not toggle it. When candidates are already loaded at
            // mount — now the common case, since Step 4 loads them eagerly in parallel with getOrder — the
            // input never transitions false->true, so without this call the option would stay permanently
            // disabled even though eligible siblings exist (the "bundle radio never enables" QA report).
            this.recomputeSigningOptionAvailability();
        }

        const existingPref = this.signingMethodPreference.currentPreference();
        if (this.wetSignOnly()) {
            this.deliveryForm.setValue({ deliveryMethod: 'wet-sign' });
            this.deliveryMethod = 'wet-sign';
            this.signingMethodPreference.setPreference('wetsign');
            this.ref.detectChanges();
        } else if (existingPref === 'wetsign') {
            this.deliveryForm.setValue({ deliveryMethod: 'wet-sign' });
            this.deliveryMethod = 'wet-sign';
            this.ref.detectChanges();
        } else {
            this.deliveryForm.setValue({ deliveryMethod: 'docu-sign' });
            this.deliveryMethod = 'docu-sign';
            this.signingMethodPreference.setPreference('eSign');
            this.ref.detectChanges();
        }

        this.subscriptions.push(
            this.deliveryForm.valueChanges.subscribe(() => {
                this.deliveryMethod = this.deliveryForm.value.deliveryMethod;
                // 'bundle' has no single-order signing preference — the bundle flow is chosen at the
                // confirm step — so only persist a preference for the single-order methods.
                if (this.deliveryMethod === 'docu-sign') {
                    this.signingMethodPreference.setPreference('eSign');
                } else if (this.deliveryMethod === 'wet-sign') {
                    this.signingMethodPreference.setPreference('wetsign');
                }
            })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.order && changes.order.currentValue) {
            const order = this.order() as BaseOrder;
            if (
                order.eSign &&
                order.eSign.envelope &&
                order.eSign.envelope.idCheckConfig === idCheckType.sms &&
                order.eSign.envelope.recipients &&
                order.eSign.envelope.recipients.find(
                    (r: eSignEnvelopeRecipient) =>
                        r.type === recipientType.investor && !r.phoneMobile
                )
            ) {
                this.wetSignOnly.set(true);
                this.deliveryForm.get('deliveryMethod')?.setValue('wet-sign');
                this.notification()?.openNotification();
            }

            this.ref.detectChanges();
        }

        // Recompute whenever the order OR the bundle candidates change.
        this.recomputeSigningOptionAvailability();
    }

    // Recompute which signing-method options are selectable from the current inputs. Bundling sends via
    // DocuSign (e-sign), so it is subject to the SAME constraints as the docu-sign option: disabled when
    // e-sign is unavailable or the order is restricted to wet-sign (e.g. an SMS-authenticated investor
    // with no mobile number) — and additionally only enabled when there are candidates to bundle with.
    // Called from both ngOnChanges and ngOnInit (see the #5582 note where the bundle option is added),
    // because the bundle option is created after the first ngOnChanges and otherwise never recomputed.
    private recomputeSigningOptionAvailability() {
        const eSignUnavailable = this.order()?.eSign?.isAvailable === false;
        this.deliveryMethods.forEach(d => {
            if (d.value === 'docu-sign') {
                d.disabled = eSignUnavailable;
            }
            if (d.value === 'bundle') {
                d.disabled = !this.hasBundleCandidates() || eSignUnavailable || this.wetSignOnly();
            }
        });
        this.ref.detectChanges();
    }

    onClickContinue() {
        this.clickContinue.emit(this.deliveryMethod);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
